import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { request } from '../../config/helpers/axios-instance';
import PaymentInvoicesPrint from './PaymentInvoicesPrint';
import AddressSlipSelectorModal from '../address-print/AddressSlipSelectorModal';
import {
    getMedicinePrintUrlsForOrder,
    getOrderMedicinePrintUrl,
    getServicePaymentItems,
    normalizePaymentItems,
} from './printPaymentItemInvoices';

/**
 * "Print Invoices" for an order, usable from a list row.
 *
 * The SAME flow as the order sidebar's Print Invoices button
 * (src/pages/all-orders/components/rightsidebar/rightsidebar_details.jsx handlePrintAllInvoices):
 * pick the name/phone for the address slip, re-fetch the order's details, then hand the
 * payment items, eVital medicine print URLs and address slip to PaymentInvoicesPrint.
 * Kept step-for-step identical so the two buttons print the same thing -- change one and
 * change the other.
 *
 * One instance per page, not per row: `startPrint(order)` targets a row, and `printUi`
 * renders the slip selector and the print frame for whichever row is active.
 *
 * `order` needs `id`, `mapUserPatient` (with User / Patient attached) and `address`.
 */
export default function useOrderInvoicePrint() {
    const [target, setTarget] = useState(null);
    const [showSlipSelector, setShowSlipSelector] = useState(false);
    const [payload, setPayload] = useState(null);

    const startPrint = (order) => {
        if (!order?.id) return;
        setTarget(order);
        setShowSlipSelector(true);
    };

    const print = async (order, override) => {
        let orderDetails = {};
        try {
            const response = await request({
                url: `userOrder/admin/orderDetails/${order.id}`,
                method: 'GET',
            });
            if (response?.data?.data) orderDetails = response.data.data;
        } catch {
            toast.error('Could not load order details for printing');
            return;
        }

        const paymentItems = normalizePaymentItems(orderDetails?.paymentItems);
        const medicinePrintUrls = getMedicinePrintUrlsForOrder(paymentItems, orderDetails);
        const serviceItems = getServicePaymentItems(paymentItems);
        const printAddress = order.address || orderDetails?.address;

        if (!medicinePrintUrls.length && !serviceItems.length && !printAddress) {
            toast.warn(
                orderDetails?.inventoryId && !getOrderMedicinePrintUrl(orderDetails)
                    ? 'Medicine invoice print URL is not available for this order'
                    : 'Nothing available to print for this order'
            );
            return;
        }

        if (!printAddress) {
            toast.warn('Address not available — invoices will print without the shipping label');
        }

        setPayload({
            paymentItems,
            sourceData: orderDetails,
            addressPrint: printAddress
                ? {
                    address: { ...printAddress, name: override?.name, phone: override?.phone },
                    orderId: order.id,
                    billNo: orderDetails?.additionalDetails?.billNumber ?? null,
                    mapUserPatient: null,
                }
                : null,
        });
    };

    const user = target?.mapUserPatient;
    const printUi = (
        <>
            {showSlipSelector && target && (
                <AddressSlipSelectorModal
                    userName={user?.User?.name || ''}
                    userPhone={user?.User?.contact_no || ''}
                    patientName={user?.Patient?.name || ''}
                    patientPhone={user?.Patient?.contact_no || ''}
                    onConfirm={(override) => {
                        setShowSlipSelector(false);
                        print(target, override);
                    }}
                    onClose={() => setShowSlipSelector(false)}
                />
            )}
            {payload && (
                <PaymentInvoicesPrint
                    paymentItems={payload.paymentItems}
                    sourceData={payload.sourceData}
                    addressPrint={payload.addressPrint}
                    onDone={() => setPayload(null)}
                />
            )}
        </>
    );

    return { startPrint, printUi };
}
